import { useCallback, useContext } from "react";
import Swal from "sweetalert2";
import { useServiceCenter } from "../contexts";
import { ServiceCenterAPI } from "../services";
import NotificationsContext from "../../../contexts/NotificationsContext";

/**
 * Hook para ações de atendimento
 */
const useServiceActions = () => {
  const { setNotify } = useContext(NotificationsContext);
  const {
    selectedContact,
    selectedConversation,
    selectedAccount,
    updateContact,
    updateConversation,
    setLoading,
  } = useServiceCenter();

  /**
   * Iniciar atendimento
   */
  const startService = useCallback(
    async (contactId = null) => {
      const id = contactId || selectedContact?.id || selectedConversation?.contact_id;

      if (!id) {
        setNotify({
          type: "warning",
          title: "Aviso",
          message: "Contato não encontrado para iniciar atendimento",
          visible: true,
        });
        return null;
      }

      try {
        setLoading({ contact: true });

        const response = await ServiceCenterAPI.startService(id);

        if (response.success) {
          // Atualizar estado do contato
          updateContact(response.contact);

          // Atualizar conversa também
          if (selectedConversation) {
            updateConversation({
              ...selectedConversation,
              service_status: response.contact.service_status,
              current_attendant_id: response.contact.current_attendant,
              current_attendant_name: response.contact.current_attendant_name,
              current_department_id: response.contact.current_department,
              current_department_name: response.contact.current_department_name,
            });
          }

          setNotify({
            type: "success",
            title: "Atendimento Iniciado",
            message: "Você está atendendo este contato",
            visible: true,
          });

          return response;
        }

        // Resposta 2xx mas sem success:true — caminho silencioso antes desta instrumentação
        console.warn(
          "[ServiceCenter][StartService] Resposta inesperada da API (sem success:true)",
          { contactId: id, response }
        );
        setNotify({
          type: "error",
          title: "Erro",
          message:
            response?.message ||
            "Resposta inesperada da API ao iniciar o atendimento",
          visible: true,
        });
        return null;
      } catch (error) {
        console.error("Erro ao iniciar atendimento:", error);

        if (error.response?.status === 409) {
          // Conflito - contato já em atendimento
          const attendantName = error.response.data?.current_attendant_name || "outro atendente";
          setNotify({
            type: "warning",
            title: "Contato em Atendimento",
            message: `Este contato já está sendo atendido por ${attendantName}`,
            visible: true,
          });
        } else {
          setNotify({
            type: "error",
            title: "Erro",
            message: error.response?.data?.message || "Não foi possível iniciar o atendimento",
            visible: true,
          });
        }

        return null;
      } finally {
        setLoading({ contact: false });
      }
    },
    [
      selectedContact?.id,
      selectedConversation,
      updateContact,
      updateConversation,
      setLoading,
      setNotify,
    ]
  );

  /**
   * Transferir atendimento
   */
  const transferService = useCallback(
    async (contactId, { attendant_id, department_id, transfer_reason }) => {

      console.log(attendant_id, department_id)
      const id = contactId || selectedContact?.id || selectedConversation?.contact_id;

      if (!id) {
        setNotify({
          type: "warning",
          title: "Aviso",
          message: "Contato não encontrado para transferir",
          visible: true,
        });
        return null;
      }

      if (!attendant_id && !department_id) {
        setNotify({
          type: "warning",
          title: "Aviso",
          message: "Selecione um atendente ou departamento para transferir",
          visible: true,
        });
        return null;
      }

      try {
        setLoading({ contact: true });

        const response = await ServiceCenterAPI.transferService(id, {
          attendant_id,
          department_id,
          transfer_reason,
        });

        if (response.success) {
          updateContact(response.contact);

          if (selectedConversation) {
            updateConversation({
              ...selectedConversation,
              service_status: response.contact.service_status,
              current_attendant_id: response.contact.current_attendant,
              current_attendant_name: response.contact.current_attendant_name,
              current_department_id: response.contact.current_department,
              current_department_name: response.contact.current_department_name,
            });
          }

          const targetName =
            response.contact.current_attendant_name ||
            response.contact.current_department_name ||
            "novo destino";

          setNotify({
            type: "success",
            title: "Transferência Realizada",
            message: `Atendimento transferido para ${targetName}`,
            visible: true,
          });

          return response;
        }
      } catch (error) {
        console.error("Erro ao transferir atendimento:", error);

        setNotify({
          type: "error",
          title: "Erro na Transferência",
          message: error.response?.data?.message || "Não foi possível transferir o atendimento",
          visible: true,
        });

        return null;
      } finally {
        setLoading({ contact: false });
      }
    },
    [selectedContact?.id, selectedConversation, updateContact, updateConversation, setLoading, setNotify]
  );

  /**
   * Finalizar atendimento.
   *
   * Aceita o payload do FinishServiceModal: { note, rating, resolution }
   * (note singular). O backend recebe `notes` (plural). Rating/resolution
   * são enviados também — backend ignora se não suporta, mas mantém o
   * dado disponível pra extensões futuras.
   *
   * NÃO mostra prompt — a confirmação é responsabilidade do caller
   * (FinishServiceModal já tem UI de notas, rating e resolution).
   */
  const finishService = useCallback(
    async (contactId = null, finishData = {}) => {
      const id = contactId || selectedContact?.id || selectedConversation?.contact_id;

      if (!id) {
        setNotify({
          type: "warning",
          title: "Aviso",
          message: "Contato não encontrado para finalizar atendimento",
          visible: true,
        });
        return null;
      }

      // Normaliza o shape do payload. FinishServiceModal manda `note` (sing);
      // backend espera `notes` (plural). Aceita ambos pra compat.
      const notes =
        typeof finishData === "string"
          ? finishData
          : finishData?.notes ?? finishData?.note ?? "";
      const payload = {
        notes,
        ...(finishData?.rating !== undefined && { rating: finishData.rating }),
        ...(finishData?.resolution !== undefined && {
          resolution: finishData.resolution,
        }),
      };

      try {
        setLoading({ contact: true });

        const response = await ServiceCenterAPI.finishService(id, payload);

        if (response.success) {
          updateContact(response.contact);

          if (selectedConversation) {
            updateConversation({
              ...selectedConversation,
              service_status: response.contact.service_status,
              current_attendant_id: null,
              current_attendant_name: null,
            });
          }

          setNotify({
            type: "success",
            title: "Atendimento Finalizado",
            message: `Duração: ${response.duration || "N/A"}`,
            visible: true,
          });

          return response;
        }

        return null;
      } catch (error) {
        console.error("Erro ao finalizar atendimento:", error);

        setNotify({
          type: "error",
          title: "Erro",
          message:
            error.response?.data?.message ||
            "Não foi possível finalizar o atendimento",
          visible: true,
        });

        return null;
      } finally {
        setLoading({ contact: false });
      }
    },
    [selectedContact?.id, selectedConversation, updateContact, updateConversation, setLoading, setNotify]
  );

  /**
   * Atualizar status do contato
   */
  const updateServiceStatus = useCallback(
    async (contactId, status) => {
      const id = contactId || selectedContact?.id || selectedConversation?.contact_id;

      if (!id) return null;

      try {
        const response = await ServiceCenterAPI.updateServiceStatus(id, status);

        if (response.success) {
          updateContact({ id, service_status: response.status });

          if (selectedConversation) {
            updateConversation({
              ...selectedConversation,
              service_status: response.status,
            });
          }

          return response;
        }
      } catch (error) {
        console.error("Erro ao atualizar status:", error);
        setNotify({
          type: "error",
          title: "Erro",
          message: "Não foi possível atualizar o status",
          visible: true,
        });
        return null;
      }
    },
    [selectedContact?.id, selectedConversation, updateContact, updateConversation, setNotify]
  );

  /**
   * Controle do bot - alterar status
   * @param {string} phone - Telefone sem +55 e caracteres especiais
   * @param {number} status - 1 = ativo, 0 = desativado
   * @param {string} uid - UID da conta WhatsApp
   */
  const updateBotStatus = useCallback(
    async (phone, status, uid) => {
      try {
        // Enviar requisição para alterar o status
        await ServiceCenterAPI.updateBotStatus(phone, status, uid);

        // A API retorna status 200 com mensagem "Status alterado com sucesso!"
        // Se chegou aqui sem erro, a requisição foi bem-sucedida
        // Usar o status enviado como o novo status confirmado
        const newStatus = status;

        // Atualizar estado local
        if (selectedConversation) {
          updateConversation({
            ...selectedConversation,
            bot_status: newStatus,
          });
        }

        // Mostrar notificação de sucesso
        setNotify({
          type: "success",
          title: newStatus === 1 ? "IA Ativada" : "IA Desativada",
          message: newStatus === 1
            ? "O agente IA está ativo para este contato"
            : "O agente IA foi pausado para este contato",
          visible: true,
        });

        return { success: true, bot_status: newStatus };
      } catch (error) {
        console.error("Erro ao alterar status do bot:", error);
        setNotify({
          type: "error",
          title: "Erro",
          message: error?.response?.data?.message || error?.response?.data || "Não foi possível alterar o status da IA",
          visible: true,
        });
        return null;
      }
    },
    [selectedConversation, updateConversation, setNotify]
  );

  /**
   * Limpar memória do agente
   */
  const clearAgentMemory = useCallback(async () => {
    if (!selectedConversation || !selectedAccount) {
      return null;
    }

    const result = await Swal.fire({
      title: "Limpar Memória do Agente",
      html: `
        Você está prestes a <b>limpar toda a memória</b> do agente para este contato.
        <br><br>
        Esta ação irá remover todo o contexto e histórico da conversa que o agente mantém em sua memória.
        <br><br>
        <b>Esta ação não irá excluir as mensagens do WhatsApp.</b>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, limpar memória!",
    });

    if (!result.isConfirmed) {
      return null;
    }

    try {
      const session = selectedAccount.phone.replace(/\D/g, "");
      const phone = selectedConversation.phone.replace(/\D/g, "");

      const response = await ServiceCenterAPI.resetAgentMemory(session, phone);

      if (response.status === "success" || response.status === "partial") {
        setNotify({
          type: "success",
          title: "Sucesso",
          message: "Memória do agente limpa com sucesso!",
          visible: true,
        });
        return response;
      } else {
        setNotify({
          type: "error",
          title: "Erro",
          message: response.error || "Não foi possível limpar a memória do agente",
          visible: true,
        });
        return null;
      }
    } catch (error) {
      console.error("Erro ao limpar memória do agente:", error);
      setNotify({
        type: "error",
        title: "Erro",
        message: "Não foi possível limpar a memória do agente",
        visible: true,
      });
      return null;
    }
  }, [selectedConversation, selectedAccount, setNotify]);

  return {
    // Actions
    startService,
    transferService,
    finishService,
    updateServiceStatus,
    updateBotStatus,
    clearAgentMemory,

    // Loading
    isLoading: false, // Pode ser expandido para estados específicos
  };
};

export default useServiceActions;
